import { useState, useCallback } from "react";

interface DownloadOptions {
  url: string;
  filename: string;
  assetType: "video" | "image";
}

interface UseDownloadReturn {
  download: (options: DownloadOptions) => Promise<void>;
  showToast: boolean;
  setShowToast: (show: boolean) => void;
  progress: number;
  isComplete: boolean;
  isError: boolean;
  assetType: "video" | "image";
}

/**
 * Reusable hook for downloading files with progress tracking.
 * Shows progress as file downloads and handles blob creation.
 */
export function useDownload(): UseDownloadReturn {
  const [showToast, setShowToast] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isError, setIsError] = useState(false);
  const [assetType, setAssetType] = useState<"video" | "image">("video");

  const download = useCallback(
    async ({ url, filename, assetType: type }: DownloadOptions) => {
      setAssetType(type);
      setShowToast(true);
      setProgress(0);
      setIsComplete(false);
      setIsError(false);

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error("Download failed");

        const contentLength = response.headers.get("content-length");
        const total = contentLength ? parseInt(contentLength, 10) : 0;

        const reader = response.body?.getReader();
        if (!reader) throw new Error("No reader available");

        const chunks: Uint8Array[] = [];
        let received = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          chunks.push(value);
          received += value.length;

          if (total > 0) {
            setProgress(Math.min(95, (received / total) * 100));
          } else {
            // Unknown size - fake progress
            setProgress((prev) => Math.min(95, prev + 5));
          }
        }

        // Combine chunks into blob
        const blob = new Blob(chunks as BlobPart[]);
        const blobUrl = URL.createObjectURL(blob);

        // Trigger download
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Cleanup
        URL.revokeObjectURL(blobUrl);

        setProgress(100);
        setIsComplete(true);
      } catch (error) {
        console.error("Download failed:", error);
        setProgress(0);
        setIsComplete(false);
        setIsError(true);
        // Keep toast open to show error
      }
    },
    [],
  );

  return {
    download,
    showToast,
    setShowToast,
    progress,
    isComplete,
    isError,
    assetType,
  };
}
